import React from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    required,
    useTranslate,
    SaveButton,
    Toolbar,
    useRedirect,
    useNotify,
} from 'react-admin';
import { Box, Typography, Alert, Card, CardContent, IconButton } from '@mui/material';
import { ArrowBack, AttachMoney } from '@mui/icons-material';
import { CURRENCY_CHOICES } from '../../types/fee.types';
import { FeeStructureForm } from './components/FeeStructureForm';
import { ValidatedSaveButton } from './components/ValidatedSaveButton';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';
import { validateFeeStructure, stripDisabledConsumptionRates } from './validation';

const FEE_TEMPLATE_KNOWN_FIELDS = new Set([
    'name', 'description', 'currency', 'feeStructure', 'isActive',
]);

const mapFeeTemplateBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const feeTemplateFieldLabelKey = (field: string): string => {
    const head = field.split('.')[0];
    return FEE_TEMPLATE_KNOWN_FIELDS.has(head)
        ? `resources.fee-templates.fields.${head}`
        : field;
};

// For now, only HKD is supported
const HKD_ONLY_CHOICES = [
    { id: 'HKD', name: 'Hong Kong Dollar (HKD)' }
];

const CreateToolbar = () => {
    const redirect = useRedirect();
    const notify = useNotify();

    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <ValidatedSaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/fee-templates')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.fee-templates.create.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5 }}>
                    {translate('resources.fee-templates.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.fee-templates.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const FeeTemplatesCreate = () => {
    const translate = useTranslate();
    const redirect = useRedirect();
    const notify = useNotify();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    const defaultValues = {
        currency: 'HKD',
        feeStructure: {
            version: '1.0',
            currency: 'HKD',
            components: {
                sessionFee: { enabled: false, amount: 0 },
                energyFee: { enabled: false, rates: [] },
                timeFee: { enabled: false, unit: 'minute', rates: [], freeMinutes: 0, maxChargeMinutes: 0 },
                idleFee: { enabled: false, graceMinutes: 15, rate: 1, unit: 'minute' },
                reservationFee: { enabled: false, amount: 0, policyId: null },
            },
            specialDates: [],
            metadata: {},
        },
    };

    return (
        <Create
            title={false}
            component="div"
            redirect="list"
            transform={stripDisabledConsumptionRates}
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.fee-templates.notifications.created', { type: 'success' });
                    redirect('/fee-templates');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapFeeTemplateBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(feeTemplateFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                defaultValues={defaultValues}
                validate={validateFeeStructure}
                mode="onChange"
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                        <Typography variant="body2">
                            {translate('resources.fee-templates.create.infoMessage')}
                        </Typography>
                    </Alert>

                    {/* Basic Information Card */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <AttachMoney sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.fee-templates.create.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="name"
                                            label={translate('resources.fee-templates.fields.name')}
                                            validate={[required()]}
                                            fullWidth
                                            helperText={translate('resources.fee-templates.create.nameHelper')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="currency"
                                            label={translate('resources.fee-templates.fields.currency')}
                                            choices={HKD_ONLY_CHOICES}
                                            validate={[required()]}
                                            defaultValue="HKD"
                                            fullWidth
                                            helperText={translate('resources.fee-templates.create.currencyHelper')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>

                                <TextInput
                                    source="description"
                                    label={translate('resources.fee-templates.fields.description')}
                                    multiline
                                    rows={2}
                                    fullWidth
                                    helperText={translate('resources.fee-templates.create.descriptionHelper')}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Fee Structure Card */}
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <AttachMoney sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.fee-templates.create.feeStructure')}
                                </Typography>
                            </Box>

                            <FeeStructureForm source="feeStructure" />
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};